<template>
	<div class="tabbed-navigation">
		<!-- Main Tabs -->
		<div class="main-tabs-container" ref="tabsContainer">
			<button
				v-for="tab in tabs"
				:key="tab.value"
				:ref="el => { if (tab.value === selectedTab) selectedTabEl = el }"
				:class="['main-tab-button', { selected: selectedTab === tab.value }]"
				@click="handleTabClick(tab.value)"
			>
				{{ tab.label }}
			</button>
		</div>

		<!-- Sub Tabs (if applicable) -->
		<transition name="sub-tabs">
			<div v-if="currentSubTabs && currentSubTabs.length > 0" class="sub-tabs-wrapper">
				<div class="sub-tabs-container">
					<button
						v-for="subTab in currentSubTabs"
						:key="subTab.value"
						:ref="el => { if (subTab.value === selectedSubTab) selectedSubTabEl = el }"
						:class="['sub-tab-button', { selected: selectedSubTab === subTab.value }]"
						@click="handleSubTabClick(subTab.value)"
					>
						{{ subTab.label }}
					</button>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
export default {
	name: 'AccountTypeFilter',
	props: {
		tabs: {
			type: Array,
			required: true,
			description: 'Array of main tab objects with { value, label, subTabs? }'
		},
		modelValue: {
			type: String,
			default: null,
			description: 'Currently selected main tab value'
		},
		subTabValue: {
			type: String,
			default: null,
			description: 'Currently selected sub tab value'
		}
	},
	emits: ['update:modelValue', 'update:subTabValue', 'tab-change', 'sub-tab-change'],
	data() {
		return {
			selectedTab: this.modelValue,
			selectedSubTab: this.subTabValue,
			selectedTabEl: null,
			selectedSubTabEl: null
		}
	},
	computed: {
		currentSubTabs() {
			const currentTab = this.tabs.find(tab => tab.value === this.selectedTab);
			return currentTab?.subTabs || null;
		}
	},
	watch: {
		modelValue(newVal) {
			this.selectedTab = newVal;
			this.scrollSelectedIntoView();
		},
		subTabValue(newVal) {
			this.selectedSubTab = newVal;
		},
		selectedTab(newVal) {
			// Reset sub tab when main tab changes
			const currentTab = this.tabs.find(tab => tab.value === newVal);
			if (currentTab?.subTabs && currentTab.subTabs.length > 0) {
				// Default to 'all' if available, otherwise first sub tab
				const defaultSubTab = currentTab.subTabs.find(st => st.value === 'all') || currentTab.subTabs[0];
				this.selectedSubTab = defaultSubTab.value;
				this.$emit('update:subTabValue', defaultSubTab.value);
			} else {
				this.selectedSubTab = null;
				this.$emit('update:subTabValue', null);
			}
		}
	},
	methods: {
		handleTabClick(tabValue) {
			this.selectedTab = tabValue;
			this.$emit('update:modelValue', tabValue);
			this.$emit('tab-change', tabValue);
			this.scrollSelectedIntoView();
		},
		handleSubTabClick(subTabValue) {
			this.selectedSubTab = subTabValue;
			this.$emit('update:subTabValue', subTabValue);
			this.$emit('sub-tab-change', subTabValue);
			this.scrollSelectedSubTabIntoView();
		},
		scrollSelectedIntoView() {
			this.$nextTick(() => {
				if (this.selectedTabEl) {
					this.selectedTabEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
				}
			});
		},
		scrollSelectedSubTabIntoView() {
			this.$nextTick(() => {
				if (this.selectedSubTabEl) {
					this.selectedSubTabEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
				}
			});
		}
	},
	mounted() {
		this.scrollSelectedIntoView();
	}
}
</script>

<style scoped>
/* Main Tabs */
.main-tabs-container {
	display: flex;
	gap: 8px;
	margin-bottom: 16px;
	overflow-x: auto;
	padding: 0 20px;
	margin-left: -30px;
	margin-right: -30px;
	padding-bottom: 4px;
	-webkit-overflow-scrolling: touch;
}

.main-tab-button {
	flex-shrink: 0;
	text-transform: none;
	padding: 12px 20px;
	background-color: white;
	font-weight: bold;
	border: none;
	cursor: pointer;
	white-space: nowrap;
	transition: all 0.2s ease;
	border-radius: 6px;
	font-size: 12px;
	line-height: 16px;
	color: #413655;
}

.main-tab-button.selected {
	background-color: #413655;
	color: white;
}

/* Hide scrollbar but keep functionality */
.main-tabs-container::-webkit-scrollbar {
	display: none;
}

.main-tabs-container {
	-ms-overflow-style: none;
	scrollbar-width: none;
}

/* Sub Tabs Wrapper */
.sub-tabs-wrapper {
	margin-bottom: 16px;
	margin-left: -30px;
	margin-right: -30px;
	padding: 0 30px;
}

/* Vue transition classes for smooth expand/collapse */
.sub-tabs-enter-active,
.sub-tabs-leave-active {
	transition: max-height 0.3s ease, opacity 0.3s ease, margin-bottom 0.3s ease;
	max-height: 60px;
}

.sub-tabs-enter-from,
.sub-tabs-leave-to {
	max-height: 0;
	opacity: 0;
	margin-bottom: 0;
}

/* Sub Tabs */
.sub-tabs-container {
	display: flex;
	gap: 8px;
	overflow-x: auto;
	padding: 0 20px;
	margin-left: -30px;
	margin-right: -30px;
	padding-bottom: 4px;
	-webkit-overflow-scrolling: touch;
}

.sub-tab-button {
	border: 1px solid rgba(65, 54, 85, 0.15);
	border-radius: 100px;
	padding: 8px 12px;
	font-size: 11px;
	line-height: 12px;
	color: var(--brand-purple);
	text-align: center;
	background: var(--background-color-main);
	white-space: nowrap;
	flex-shrink: 0;
	transition: background-color 0.2s ease;
}

.sub-tab-button.selected {
	background: white;
	border-color: var(--brand-purple);
	color: var(--brand-purple);
	font-weight: bold;
}

/* Hide scrollbar for sub tabs */
.sub-tabs-container::-webkit-scrollbar {
	display: none;
}

.sub-tabs-container {
	-ms-overflow-style: none;
	scrollbar-width: none;
}
</style>
